import React, { useEffect, useMemo, useState } from 'react';
import { ShieldCheck, Save, UserPlus, ChevronDown, ChevronUp, TrendingUp, BarChart2, Link2, Copy, Power, Trash2 } from 'lucide-react';
import { apiClient } from '../apiClient';

const AdminPage = () => {
  const [features, setFeatures] = useState([]);
  const [levels, setLevels] = useState(['starter', 'pro', 'moderator', 'admin']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignStatus, setAssignStatus] = useState('');
  const [assignForm, setAssignForm] = useState({ email: '', level: 'starter' });
  const [accessOpen, setAccessOpen] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [range, setRange] = useState('this_month');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [inviteLinks, setInviteLinks] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteStatus, setInviteStatus] = useState('');
  const [inviteForm, setInviteForm] = useState({
    name: '',
    note: '',
    tier: 'pro',
    max_uses: '1',
    expires_at: '',
    is_active: true
  });

  const fetchMatrix = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await apiClient.get('/api/admin/access-levels');
      setFeatures(result.features || []);
      setLevels(result.levels || ['starter', 'pro', 'moderator', 'admin']);
    } catch (err) {
      setError(err.message || 'Unable to load access levels.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatrix();
  }, []);

  const fetchInviteLinks = async () => {
    setInviteLoading(true);
    try {
      const data = await apiClient.get('/api/admin/invite-tokens');
      setInviteLinks(data || []);
    } catch (err) {
      setError(err.message || 'Unable to load invite links.');
    } finally {
      setInviteLoading(false);
    }
  };

  const fetchAnalytics = async (rangeKey = range) => {
    setAnalyticsLoading(true);
    try {
      const params = { range: rangeKey };
      if (rangeKey === 'custom') {
        params.start_date = customRange.start || undefined;
        params.end_date = customRange.end || undefined;
      }
      const data = await apiClient.get('/api/admin/analytics', params);
      setAnalytics(data);
    } catch (err) {
      setError(err.message || 'Unable to load analytics.');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchInviteLinks();
  }, []);

  const groupedFeatures = useMemo(() => {
    const groups = {};
    (features || []).forEach((feature) => {
      const group = feature.group || 'General';
      if (!groups[group]) groups[group] = [];
      groups[group].push(feature);
    });
    return groups;
  }, [features]);

  const formatMoney = (value) => {
    const amount = Number(value || 0);
    if (!Number.isFinite(amount)) return '$0';
    return `$${amount.toLocaleString(undefined, { minimumFractionDigits: amount % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
  };

  const handleToggle = (featureKey, level, checked) => {
    setFeatures((prev) =>
      prev.map((feature) => {
        if (feature.feature_key !== featureKey) return feature;
        return {
          ...feature,
          levels: {
            ...(feature.levels || {}),
            [level]: checked
          }
        };
      })
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus('');
    try {
      await apiClient.put('/api/admin/access-levels', { features });
      setStatus('Access settings updated.');
    } catch (err) {
      setStatus(err.message || 'Failed to update access settings.');
    } finally {
      setSaving(false);
    }
  };

  const seriesPoints = useMemo(() => {
    const series = analytics?.series || [];
    if (!series.length) return '';
    const values = series.map((item) =>
      (item.prompts || 0) + (item.posts || 0) + (item.comments || 0) + (item.outputs || 0)
    );
    const max = Math.max(...values, 1);
    const points = series.map((item, index) => {
      const x = (index / (series.length - 1 || 1)) * 100;
      const y = 100 - (values[index] / max) * 80;
      return `${x},${y}`;
    });
    return points.join(' ');
  }, [analytics]);

  const handleAssign = async (event) => {
    event.preventDefault();
    setAssignStatus('');
    if (!assignForm.email.trim()) {
      setAssignStatus('Please enter an email address.');
      return;
    }
    try {
      setAssigning(true);
      await apiClient.post('/api/admin/users/level', {
        email: assignForm.email.trim(),
        level: assignForm.level
      });
      setAssignStatus('Account level updated.');
      setAssignForm({ email: '', level: assignForm.level });
    } catch (err) {
      setAssignStatus(err.message || 'Unable to update account level.');
    } finally {
      setAssigning(false);
    }
  };

  const resetInviteForm = () => {
    setInviteForm({
      name: '',
      note: '',
      tier: 'pro',
      max_uses: '1',
      expires_at: '',
      is_active: true
    });
  };

  const handleCreateInvite = async (event) => {
    event.preventDefault();
    setInviteStatus('');
    if (!inviteForm.name.trim()) {
      setInviteStatus('Please enter a name for this invite link.');
      return;
    }
    try {
      setInviteSaving(true);
      await apiClient.post('/api/admin/invite-tokens', {
        name: inviteForm.name.trim(),
        note: inviteForm.note.trim() || null,
        tier: inviteForm.tier,
        max_uses: inviteForm.max_uses ? Number(inviteForm.max_uses) : 1,
        expires_at: inviteForm.expires_at || null,
        is_active: inviteForm.is_active
      });
      setInviteStatus('Invite link created.');
      resetInviteForm();
      await fetchInviteLinks();
    } catch (err) {
      setInviteStatus(err.message || 'Unable to create invite link.');
    } finally {
      setInviteSaving(false);
    }
  };

  const handleCopyInvite = async (invite) => {
    try {
      await navigator.clipboard.writeText(invite.signup_url);
      setInviteStatus(`Copied ${invite.name || 'invite link'} to clipboard.`);
    } catch (_err) {
      setInviteStatus('Unable to copy invite link.');
    }
  };

  const handleToggleInvite = async (invite) => {
    setInviteStatus('');
    try {
      await apiClient.patch(`/api/admin/invite-tokens/${invite.token}`, {
        is_active: !invite.is_active
      });
      setInviteStatus(`Invite link ${invite.is_active ? 'deactivated' : 'activated'}.`);
      await fetchInviteLinks();
    } catch (err) {
      setInviteStatus(err.message || 'Unable to update invite link.');
    }
  };

  const handleDeleteInvite = async (invite) => {
    const confirmed = window.confirm(`Delete invite link "${invite.name || invite.token}"?`);
    if (!confirmed) return;
    setInviteStatus('');
    try {
      await apiClient.delete(`/api/admin/invite-tokens/${invite.token}`);
      setInviteStatus('Invite link deleted.');
      await fetchInviteLinks();
    } catch (err) {
      setInviteStatus(err.message || 'Unable to delete invite link.');
    }
  };

  const formatDateTime = (value) => {
    if (!value) return 'Never';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Never';
    return date.toLocaleString();
  };

  const getInviteTitle = (invite) => {
    return invite.name || invite.note || `Invite ${String(invite.token || '').slice(0, 8)}`;
  };

  const getMaxUsesLabel = (invite) => {
    const value = Number(invite.max_uses);
    if (Number.isFinite(value) && value > 0) return value;
    return 'Unlimited';
  };

  const getSubscriptionLabel = (user) => {
    if (user?.active_stripe_subscription) {
      return `Active${user?.stripe_subscription_plan ? ` · ${user.stripe_subscription_plan}` : ''}`;
    }
    if (user?.stripe_customer_connected) {
      return `No active subscription${user?.stripe_subscription_status && user.stripe_subscription_status !== 'none' ? ` · ${user.stripe_subscription_status}` : ''}`;
    }
    return 'No Stripe subscription';
  };

  const getInviteSourceLabel = (user) => {
    if (!user?.joined_via_invite) return 'Direct signup';
    return user.invite_name || user.invite_token || 'Invite link';
  };

  const getDisplayLevel = (user) => {
    return String(user?.display_account_level || user?.account_tier || 'starter').trim().toLowerCase();
  };

  const getLevelPillClass = (user) => {
    const level = getDisplayLevel(user);
    if (level === 'admin') return 'admin';
    if (level === 'pro') return 'pro';
    return 'standard';
  };

  return (
    <div className="min-h-screen admin-page">
      <div className="page-header">
        <div>
          <h1 className="text-3xl font-bold">Admin</h1>
          <p className="text-gray-400">Manage account access levels and feature permissions.</p>
        </div>
      </div>

      <div className="admin-grid">
        <div className="card-glass admin-card">
          <div className="admin-card-header">
            <BarChart2 className="w-5 h-5 text-rebel-blue" />
            <div>
              <h2 className="text-xl font-bold">Usage & Revenue Analytics</h2>
              <p className="settings-meta">Track growth, content creation, and platform activity.</p>
            </div>
          </div>

          <div className="admin-analytics-toolbar">
            <div className="sort-field">
              <span className="sort-label">Range</span>
              <select
                className="input-glass"
                value={range}
                onChange={(event) => setRange(event.target.value)}
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="last_3_months">Last 3 Months</option>
                <option value="last_6_months">Last 6 Months</option>
                <option value="this_year">This Year</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
            {range === 'custom' && (
              <div className="admin-analytics-custom">
                <input
                  className="input-glass"
                  type="date"
                  value={customRange.start}
                  onChange={(event) => setCustomRange((prev) => ({ ...prev, start: event.target.value }))}
                />
                <input
                  className="input-glass"
                  type="date"
                  value={customRange.end}
                  onChange={(event) => setCustomRange((prev) => ({ ...prev, end: event.target.value }))}
                />
              </div>
            )}
            <button className="btn-secondary" type="button" onClick={() => fetchAnalytics(range)} disabled={analyticsLoading}>
              {analyticsLoading ? 'Refreshing...' : 'Apply'}
            </button>
          </div>

            <div className="admin-analytics-grid">
              <div className="admin-mrr-card">
                <div className="admin-mrr-label">Current MRR</div>
                <div className="admin-mrr-value">{formatMoney(analytics?.mrr?.amount || 0)}</div>
                <div className="admin-mrr-sub">
                  Active subs: {analytics?.mrr?.active_subscriptions || 0}
                  {' · '}
                  Paid this range: {formatMoney(analytics?.revenue?.total || 0)}
                </div>
                <div className="admin-mrr-sub">
                  Starter: {analytics?.mrr?.starter || 0}
                  {' · '}
                  Pro: {analytics?.mrr?.pro || 0}
                  {analytics?.mrr?.other ? ` · Other: ${analytics.mrr.other}` : ''}
                </div>
                <div className="admin-mrr-sub">
                  Source: {analytics?.mrr?.source === 'stripe' ? 'Stripe live data' : 'Database fallback'}
                </div>
              </div>
            <div className="admin-analytics-chart">
              <div className="chart-header">
                <TrendingUp className="w-4 h-4" />
                <span>Activity Trend</span>
              </div>
              {seriesPoints ? (
                <svg viewBox="0 0 100 100" className="chart-svg" preserveAspectRatio="none">
                  <polyline points={seriesPoints} fill="none" stroke="url(#chartGradient)" strokeWidth="2" />
                  <defs>
                    <linearGradient id="chartGradient" x1="0" x2="1" y1="0" y2="0">
                      <stop offset="0%" stopColor="#ef4444" />
                      <stop offset="100%" stopColor="#3b82f6" />
                    </linearGradient>
                  </defs>
                </svg>
              ) : (
                <div className="chart-empty">No activity yet.</div>
              )}
            </div>
            <div className="admin-stats-row">
              <div className="stat-card">
                <div className="stat-label">Users</div>
                <div className="stat-value">{analytics?.totals?.users || 0}</div>
                <div className="stat-sub">+{analytics?.new_counts?.users || 0} new</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Prompts</div>
                <div className="stat-value">{analytics?.totals?.prompts || 0}</div>
                <div className="stat-sub">+{analytics?.new_counts?.prompts || 0} new</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Projects</div>
                <div className="stat-value">{analytics?.totals?.projects || 0}</div>
                <div className="stat-sub">+{analytics?.new_counts?.projects || 0} new</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Social Posts</div>
                <div className="stat-value">{analytics?.totals?.posts || 0}</div>
                <div className="stat-sub">+{analytics?.new_counts?.posts || 0} new</div>
              </div>
            </div>
          </div>
        </div>

        <div className="card-glass admin-card admin-card-full">
          <div className="admin-card-header">
            <UserPlus className="w-5 h-5 text-rebel-blue" />
            <div>
              <h2 className="text-xl font-bold">User Directory</h2>
              <p className="settings-meta">Live Supabase users, tiers, invite source, and Stripe subscription status.</p>
            </div>
          </div>

          <div className="admin-user-summary">
            <div className="stat-card">
              <div className="stat-label">Total Users</div>
              <div className="stat-value">{analytics?.totals?.users || 0}</div>
              <div className="stat-sub">All users in Supabase</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Starter</div>
              <div className="stat-value">{analytics?.users_by_tier?.starter || 0}</div>
              <div className="stat-sub">Starter accounts</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Pro</div>
              <div className="stat-value">{analytics?.users_by_tier?.pro || 0}</div>
              <div className="stat-sub">Pro accounts</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Active Stripe</div>
              <div className="stat-value">{analytics?.user_subscription_summary?.active || 0}</div>
              <div className="stat-sub">Active paid subscriptions</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Invite Joined</div>
              <div className="stat-value">{analytics?.user_subscription_summary?.invite_joined || 0}</div>
              <div className="stat-sub">Users from invite links</div>
            </div>
          </div>

          <div className="admin-user-table">
            <div className="admin-user-row header">
              <div>User</div>
              <div>Level</div>
              <div>Invite Source</div>
              <div>Stripe</div>
              <div>Joined</div>
            </div>
            {(analytics?.users || []).map((user) => (
              <div key={user.user_id} className="admin-user-row">
                <div className="admin-user-primary">
                  <div className="admin-user-name">{user.name || user.username || 'User'}</div>
                  <div className="admin-user-email">{user.email}</div>
                </div>
                <div>
                  <span className={`account-level-pill ${getLevelPillClass(user)}`}>
                    {getDisplayLevel(user)}
                  </span>
                </div>
                <div className="admin-user-meta">
                  <div>{getInviteSourceLabel(user)}</div>
                  {user.invite_redeemed_at ? <span>Redeemed: {formatDateTime(user.invite_redeemed_at)}</span> : null}
                </div>
                <div className="admin-user-meta">
                  <div>{getSubscriptionLabel(user)}</div>
                  {user.stripe_subscription_current_period_end ? (
                    <span>Renews through: {formatDateTime(user.stripe_subscription_current_period_end)}</span>
                  ) : null}
                </div>
                <div className="admin-user-meta">
                  <div>{formatDateTime(user.created_at)}</div>
                  <span>{user.username ? `@${user.username}` : ''}</span>
                </div>
              </div>
            ))}
            {!analytics?.users?.length && (
              <div className="chart-empty">No users found.</div>
            )}
          </div>
        </div>

        <div className="card-glass admin-card">
          <div className="admin-card-header admin-collapsible" onClick={() => setAccessOpen((prev) => !prev)}>
            <ShieldCheck className="w-5 h-5 text-rebel-blue" />
            <div>
              <h2 className="text-xl font-bold">Account Level Access Settings</h2>
              <p className="settings-meta">Toggle which account tiers can access each feature.</p>
            </div>
            <div className="admin-collapse-icon">
              {accessOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          </div>

          {accessOpen && (
            <>
              {error && <div className="error-banner">{error}</div>}
              {loading ? (
                <div className="skeleton-line" />
              ) : (
                <div className="admin-access-list">
                  {Object.entries(groupedFeatures).map(([group, items]) => (
                    <div key={group} className="admin-access-group">
                      <div className="admin-access-group-title">{group}</div>
                      <div className="admin-access-table">
                        <div className="admin-access-row header">
                          <div className="feature-col">Feature</div>
                          {levels.map((level) => (
                            <div key={level} className="level-col">{level}</div>
                          ))}
                        </div>
                        {items.map((feature) => (
                          <div key={feature.feature_key} className="admin-access-row">
                            <div className="feature-col">
                              <div className="feature-title">{feature.label}</div>
                              {feature.description && <div className="feature-sub">{feature.description}</div>}
                            </div>
                            {levels.map((level) => (
                              <div key={level} className="level-col">
                                <input
                                  type="checkbox"
                                  checked={Boolean(feature.levels?.[level])}
                                  onChange={(event) =>
                                    handleToggle(feature.feature_key, level, event.target.checked)
                                  }
                                />
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="admin-actions">
                {status && <div className="success-banner">{status}</div>}
                <button className="btn-primary" onClick={handleSave} disabled={saving || loading}>
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </>
          )}
        </div>

        <div className="card-glass admin-card">
          <div className="admin-card-header">
            <UserPlus className="w-5 h-5 text-rebel-blue" />
            <div>
              <h2 className="text-xl font-bold">Grant Account Level</h2>
              <p className="settings-meta">Assign a level to a user by email.</p>
            </div>
          </div>

          <form className="settings-form" onSubmit={handleAssign}>
            {assignStatus && <div className="success-banner">{assignStatus}</div>}
            <div className="modal-field">
              <label>User Email</label>
              <input
                className="input-glass"
                type="email"
                value={assignForm.email}
                onChange={(event) => setAssignForm((prev) => ({ ...prev, email: event.target.value }))}
                placeholder="user@rebelprompts.com"
              />
            </div>
            <div className="modal-field">
              <label>Account Level</label>
              <select
                className="input-glass"
                value={assignForm.level}
                onChange={(event) => setAssignForm((prev) => ({ ...prev, level: event.target.value }))}
              >
                {levels.map((level) => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>
            <button className="btn-primary" type="submit" disabled={assigning}>
              {assigning ? 'Updating...' : 'Update Level'}
            </button>
          </form>
        </div>

        <div className="card-glass admin-card admin-card-full">
          <div className="admin-card-header">
            <Link2 className="w-5 h-5 text-rebel-blue" />
            <div>
              <h2 className="text-xl font-bold">Invite Links</h2>
              <p className="settings-meta">Create, track, deactivate, and manage signup links for free access campaigns.</p>
            </div>
          </div>

          <div className="invite-tool-grid">
            <form className="settings-form invite-create-form" onSubmit={handleCreateInvite}>
              {inviteStatus && <div className="success-banner">{inviteStatus}</div>}
              <div className="modal-field">
                <label>Link Name</label>
                <input
                  className="input-glass"
                  value={inviteForm.name}
                  onChange={(event) => setInviteForm((prev) => ({ ...prev, name: event.target.value }))}
                  placeholder="Skool Member Access"
                />
              </div>
              <div className="modal-field">
                <label>Account Tier</label>
                <select
                  className="input-glass"
                  value={inviteForm.tier}
                  onChange={(event) => setInviteForm((prev) => ({ ...prev, tier: event.target.value }))}
                >
                  {levels.map((level) => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>
              <div className="modal-field">
                <label>Max Uses</label>
                <input
                  className="input-glass"
                  type="number"
                  min="1"
                  value={inviteForm.max_uses}
                  onChange={(event) => setInviteForm((prev) => ({ ...prev, max_uses: event.target.value }))}
                  placeholder="1"
                />
              </div>
              <div className="modal-field">
                <label>Expires At</label>
                <input
                  className="input-glass"
                  type="datetime-local"
                  value={inviteForm.expires_at}
                  onChange={(event) => setInviteForm((prev) => ({ ...prev, expires_at: event.target.value }))}
                />
              </div>
              <div className="modal-field">
                <label>Notes</label>
                <textarea
                  className="input-glass"
                  rows={4}
                  value={inviteForm.note}
                  onChange={(event) => setInviteForm((prev) => ({ ...prev, note: event.target.value }))}
                  placeholder="Who this link is for, campaign details, or restrictions."
                />
              </div>
              <label className="admin-checkbox-row">
                <input
                  type="checkbox"
                  checked={inviteForm.is_active}
                  onChange={(event) => setInviteForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                />
                <span>Create as active</span>
              </label>
              <button className="btn-primary" type="submit" disabled={inviteSaving}>
                {inviteSaving ? 'Creating...' : 'Create Invite Link'}
              </button>
            </form>

            <div className="invite-links-panel">
              <div className="invite-links-toolbar">
                <div className="settings-meta">Live invite inventory</div>
                <button className="btn-secondary" type="button" onClick={fetchInviteLinks} disabled={inviteLoading}>
                  {inviteLoading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
              {inviteLoading ? (
                <div className="skeleton-line" />
              ) : inviteLinks.length === 0 ? (
                <div className="chart-empty">No invite links created yet.</div>
              ) : (
                <div className="invite-link-list">
                  {inviteLinks.map((invite) => (
                    <div key={invite.token} className="invite-link-card">
                      <div className="invite-link-head">
                        <div>
                          <div className="invite-link-title-row">
                            <div className="invite-link-title">{getInviteTitle(invite)}</div>
                            <span className={`invite-status-pill ${invite.status}`}>{invite.status}</span>
                          </div>
                          <div className="invite-link-meta">
                            Tier: {invite.tier} · Uses: {invite.uses_count ?? 0}/{getMaxUsesLabel(invite)} · Views: {invite.views_count ?? 0}
                          </div>
                        </div>
                        <div className="invite-link-actions">
                          <button className="btn-secondary btn-compact" type="button" onClick={() => handleCopyInvite(invite)}>
                            <Copy className="w-4 h-4" /> Copy
                          </button>
                          <button className="btn-secondary btn-compact" type="button" onClick={() => handleToggleInvite(invite)}>
                            <Power className="w-4 h-4" /> {invite.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button className="btn-secondary btn-compact danger" type="button" onClick={() => handleDeleteInvite(invite)}>
                            <Trash2 className="w-4 h-4" /> Delete
                          </button>
                        </div>
                      </div>
                      {invite.note && <div className="invite-link-note">{invite.note}</div>}
                      <div className="invite-link-url">{invite.signup_url}</div>
                      <div className="invite-link-stats">
                        <span>Created: {formatDateTime(invite.created_at)}</span>
                        <span>Expires: {formatDateTime(invite.expires_at)}</span>
                        <span>Last viewed: {formatDateTime(invite.last_viewed_at)}</span>
                        <span>Last used: {formatDateTime(invite.last_used_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminPage;
